import {AxiosError} from "axios";
import {ApiConfig} from "components/src/api/portal/ApiConfig";
import {PDFApi, PdfReplyParameters} from "components/src/api/portal/generated";
import {AccessibleSpinner} from "components/src/common/AccessibleSpinner";
import {Attachments} from "components/src/common/Attachments";
import {ConfirmDialog} from "components/src/common/ConfirmDialog";
import {LocaleSelector} from "components/src/common/LocaleSelector";
import {RenderAlert} from "components/src/common/RenderAlert";
import {useSpinnerStatusContext} from "components/src/hook/UseSpinnerStatusContext";
import {useTranslationWithOverride} from "components/src/hook/UseTranslationWithOverride";
import {logger} from "components/src/utils/Logger";
import {StringUtils} from "components/src/utils/StringUtils";
import {Utils} from "components/src/utils/Utils";
import {Attachment as AttachmentIcon, SendDiagonal, Trash as DeleteIcon} from "iconoir-react";
import {useEffect, useRef, useState} from "react";
import Button from 'react-bootstrap/Button';
import FloatingLabel from 'react-bootstrap/FloatingLabel';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import Spinner from "react-bootstrap/Spinner";
import {useSearchParams} from "react-router-dom";

/*
 * REST api for accessing the back-end
 */
const pdfApi = new PDFApi(ApiConfig.config());

export const PDFReply = () =>
{
    /**
     * searchParams is used to get the env and hmac parameters from the reply URL
     */
    const [searchParams] = useSearchParams();
    /**
     * The env URL parameter
     */
    const [env, setEnv] = useState<string>();
    /**
     * The HMAC URL parameter
     */
    const [hmac, setHmac] = useState<string>();
    /**
     * The PDF reply parameters
     */
    const [replyParameters, setReplyParameters] = useState<PdfReplyParameters>();
    /**
     * Body of the reply
     */
    const [body, setBody] = useState<string>("");
    /**
     * Attachments
     */
    const [attachments, setAttachments] = useState<File[]>([]);
    /**
     * The error message if some error occurred which is not a validation error
     */
    const [errorMessage, setErrorMessage] = useState<string>();
    /**
     * Keeps track of activities for which the spinner should be shown. If an activity is started, the counter should
     * be increased by one and after the activity
     */
    const {spinnerStateCounter, setSpinnerStateCounter} = useSpinnerStatusContext();
    /**
     * Set to true while sending to show a spinner on the download button
     */
    const [sending, setSending] = useState<boolean>(false);
    /**
     * Set to true if the message was sent
     */
    const [sent, setSent] = useState<boolean>(false);
    /**
     * Set to true when the user has made any changes to the form
     */
    const [isDirty, setIsDirty] = useState<boolean>(false);
    /**
     * Set to true to show the discard-changes confirmation dialog
     */
    const [showDiscardConfirm, setShowDiscardConfirm] = useState<boolean>(false);
    /**
     * Reference to the <textarea> element so it can receive focus
     */
    const bodyRef = useRef(null as HTMLTextAreaElement | null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    /**
     * i18next translation hook
     */
    const {t} = useTranslationWithOverride();

    useEffect(() =>
    {
        const env = searchParams.get("env");
        const hmac = searchParams.get("hmac");

        if (env === null || hmac === null)
        {
            logger.warn("env or hmac parameter missing");
            setErrorMessage(t('portal.pdf-reply.invalid-reply-url'));

            return;
        }

        logger.debug("Validating reply parameters")

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        pdfApi.pDFControllerParsePdfReplyParameters(env, hmac)
            .then(response =>
            {
                setEnv(env);
                setHmac(hmac);

                setReplyParameters(response.data);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error validating reply parameters", err);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            })
    }, [searchParams,
        setSpinnerStateCounter,
        t]);

    const sendReply = () =>
    {
        if (env === undefined || hmac === undefined) {
            throw new Error("Invalid state")
        }

        // reset error message
        setErrorMessage(undefined);

        setSending(true);

        pdfApi.pDFControllerSendPdfReply(env, hmac, body, attachments).then(() => {
            logger.debug("Email sent")
            setSent(true);
            setIsDirty(false);
        })
        .catch((err: Error | AxiosError) => {
            logger.error("Error sending reply", err);
            setErrorMessage(Utils.getErrorMessage(err, t));
        })
        .finally(() => {
            setSending(false);
        });
    }

    function addAttachments(fileList : FileList)
    {
        setIsDirty(true);
        setAttachments(prevAttachments => [...prevAttachments, ...Array.from(fileList)]);
        setSent(false);
    }

    function removeAttachment(file : File)
    {
        setAttachments(prevAttachments => prevAttachments.filter(f => f !== file));
        setSent(false);
    }

    const closeDialog = () => {
        window.location.href = 'about:blank';
    }

    const onHideDialog = () => {
        if (isDirty) {
            setShowDiscardConfirm(true);
        } else {
            closeDialog();
        }
    }

    return (
        <div>
            <Modal show={true}
                   onHide={() => onHideDialog()}
                   backdrop="static"
                   scrollable={true}
                   dialogClassName={"cm-modal-dialog"}
                   contentClassName={"cm-modal-content"}
                   onEntered={() => {bodyRef.current?.focus()}}
            >
                    <Modal.Header closeButton>
                        <Modal.Title>
                            {t('portal.pdf-reply.title')}
                        </Modal.Title>

                        <div className="d-flex flex-fill justify-content-end align-items-center">
                            <AccessibleSpinner show={spinnerStateCounter > 0} variant="primary"/>
                            <LocaleSelector className="w-auto"/>
                        </div>
                    </Modal.Header>

                    <Modal.Body className="d-flex flex-column">
                        <div className="row flex-grow-1">
                            <main className="col d-flex flex-column overflow-auto modal-main-body">
                                <RenderAlert message={errorMessage} setMessage={setErrorMessage}
                                             tooltipPlacement={"bottom"}/>

                                {replyParameters !== undefined && replyParameters.expired &&
                                    <RenderAlert message={t('portal.pdf-reply.reply-link-expired')} variant="danger"/>
                                }

                                {replyParameters !== undefined && !replyParameters.replyAllowed &&
                                    <RenderAlert message={t('portal.pdf-reply.reply-not-allowed')} variant="danger"/>
                                }

                                {sent &&
                                    <RenderAlert message={t('portal.pdf-reply.reply-sent')} variant="success"/>
                                }

                                <div className="d-flex flex-column flex-grow-1">
                                    <FloatingLabel label={t('portal.messages.subject')} className="text-secondary mb-3">
                                        <Form.Control type="text" value={replyParameters?.subject} placeholder="Subject"
                                                      readOnly={false}/>
                                    </FloatingLabel>

                                    <FloatingLabel label={t('portal.messages.from')} className="text-secondary mb-3">
                                        <Form.Control type="text" value={replyParameters?.from} placeholder="From"
                                                      readOnly={false}/>
                                    </FloatingLabel>

                                    <FloatingLabel label={t('portal.messages.to')} className="text-secondary mb-3">
                                        <Form.Control type="text" value={replyParameters?.to} placeholder="To"
                                                      readOnly={false}/>
                                    </FloatingLabel>

                                    {!StringUtils.isBlank(replyParameters?.cc) &&
                                        <FloatingLabel label={t('portal.messages.cc')} className="text-secondary mb-3">
                                            <Form.Control type="text" value={replyParameters?.cc} placeholder="Cc"
                                                          readOnly={false}/>
                                        </FloatingLabel>
                                    }

                                    <Form.Group className="mb-3 d-flex flex-column flex-grow-1">
                                        <Form.Control
                                            className="cm-mail-textarea flex-grow-1"
                                            as="textarea"
                                            onChange={e => { setIsDirty(true); setBody(e.target.value); }}
                                            value={body}
                                            ref={bodyRef}
                                        />
                                    </Form.Group>

                                    <Attachments
                                        attachments={attachments}
                                        dropdownItemText={t('portal.messages.mail.remove-attachment-title')}
                                        onDropdownItemClick={(index) => removeAttachment(attachments[index])}
                                        icon={<DeleteIcon className="me-1"/>}
                                    />
                                </div>
                            </main>
                        </div>
                    </Modal.Body>

                <Modal.Footer className="justify-content-between">
                    <div>
                        {/*
                     * The native file input is hidden (d-none) because it cannot be
                     * styled consistently across browsers. The Button below acts as
                     * the visible trigger and programmatically opens the file picker
                     * via fileInputRef.current?.click().
                     */}
                        <input
                            ref={fileInputRef}
                            type="file"
                            multiple={true}
                            accept=""
                            className="d-none"
                            onChange={e => addAttachments((e.target.files as FileList))}
                        />
                        <Button variant="outline-light" className="text-decoration-none text-secondary"
                                onClick={() => fileInputRef.current?.click()}>
                            <AttachmentIcon/> {t('portal.messages.mail.add-attachment-label')}
                        </Button>
                    </div>
                    <div className="d-flex gap-2">
                        <Button variant="outline-secondary"
                                onClick={() => onHideDialog()}>
                            {t('portal.messages.mail.cancel')}
                        </Button>
                        <Button variant="success"
                                onClick={sendReply}
                                disabled={StringUtils.isBlank(body) || spinnerStateCounter > 0}>
                            <SendDiagonal className="me-2"/>
                            {sending && <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                aria-hidden="true"
                                className="me-1"
                            />}
                            {sending ? t('portal.messages.mail.sending') : t('portal.messages.mail.send')}
                        </Button>
                    </div>
                </Modal.Footer>
            </Modal>

            <ConfirmDialog
                title={t('common.confirm')}
                bodyText={t('portal.messages.mail.discard-unsaved-warning')}
                confirmButtonTitle={t('portal.messages.mail.discard-unsaved-confirm')}
                onClickConfirmEvent={() => closeDialog()}
                showConfirm={showDiscardConfirm}
                setShowConfirm={setShowDiscardConfirm}
                buttonVariant="warning"
            />
        </div>
    )
}